import React, { Component, createRef } from 'react';
import { omit, get, debounce, has, isEmpty } from 'lodash';
import { Input, Tooltip } from 'antd';
import { escape as htmlEscape, unescape as htmlUnescape } from 'html-escaper';
import { looksEscaped } from 'APP_ROOT/utils/request';
import propsHasChanged from 'APP_ROOT/utils/propsHasChanged';
import { getEnvironmentVariable } from 'APP_ROOT/appVersion';
import getLastStatus from 'APP_ROOT/utils/get-report-status';
import ReviewerField from '../reviewer-field';
import { connect } from 'react-redux';
import { getSourceFieldValue } from '../../../selectors/form';
import { autoSaveDraftReport } from '../../utils/auto-save';
import getRepeaterPropsForAutoSave from '../forms/getRepeaterPropsForAutoSave';
import { TEXT } from '../../../constants/fieldTypes';
import { getMapFieldInfo } from '../maps/helper.js';
import MapComponent from '../maps/mapComponent';
import { hasFeatures, FEATURES } from '../../../utils/features';
import StyledInputWrapper, { StyledMapIcon } from '../styled/input-text';
import StyledIcon from '../styled/icon';
import { DISPLAY_FROM } from '../../../modules/FormBuilder/constants/constants.js';

const AUTO_SAVE_TIME = getEnvironmentVariable('AUTO_SAVE_TIME', 30);
const pathRegex = /^(?:data\.)?(?:tab\[(?<tabIndex>\d+)\]\.)?(?:(?<repeaterKey>.+)\[(?<repeaterIndex>\d+)\]\.)?(?<fieldKey>.+)$/;
function getValue(key, formData) {
  const { repeaterKey, repeaterIndex, fieldKey } =
    key.match(pathRegex)?.groups || {};

  // default root value
  let value = formData[fieldKey];
  if (repeaterKey) {
    let repeater = formData[repeaterKey];
    let repeaterItem = repeater?.[parseInt(repeaterIndex)];
    value = repeaterItem?.[fieldKey];
  }

  return value || '';
}

class InputText extends Component {
  inputRef = createRef();
  constructor(props) {
    super(props);

    let autocomplete;
    let renderMap = false;
    let disableInputAddress = false;
    const { currentUser: { featureFlags = [] } = {}, field_type } = props;
    // make sure we are rendering a text field
    const isTextField = field_type === TEXT;

    // sometimes masked field uses this component
    this.enableAddressVerification =
      isTextField && hasFeatures(featureFlags, FEATURES.addressVerification);

    this.state = {
      autocomplete,
      renderMap,
      disableInputAddress,
      showMap: false,
      mapData: {},
      prevMapData: {},
      clearMap: false,
      shouldClearMap: false,
      setNewCenterMap: false,
      locationInitialized: false,
      mapFields: {},
      isMainMapField: false,
      isMapField: false,
    };

    if (this.enableAddressVerification) {
      const mapFieldInfo = getMapFieldInfo(this.inputRef, props.title);
      const isMapField = mapFieldInfo.isMapField;
      this.addressTypes = mapFieldInfo.types;

      if (isMapField) {
        window.addEventListener(
          'form-component-map-value-change',
          this.onMapValueChange
        );
        this.state.disableInputAddress = true;
        this.state.isMapField = isMapField;
        this.state.isMainMapField = mapFieldInfo.isMainMapField;
      }
    }
  }

  componentDidMount() {
    const {
      calculatedValue,
      onChange,
      value,
      isOwnChange,
      dependsOnChange,
      isReviewer,
      sourceFieldValue,
    } = this.props;

    if (!isReviewer) {
      if (
        calculatedValue &&
        ((!isOwnChange && dependsOnChange) || value === undefined)
      ) {
        onChange(calculatedValue);
      }
      if (sourceFieldValue) {
        onChange(sourceFieldValue);
      }
    }
  }

  onMapValueChange = ({ detail }) => {
    if (Object.hasOwn(detail, this.props.id)) {
      const value = detail[this.props.id];
      // Update input display immediately
      this.inputRef.current.setState({ value });

      // Cancel any pending debounced save and trigger immediate save via onBlur.
      // onBlur persists to Redux (selectedForm.data), which is what the form
      // re-reads when tabs switch and the component remounts. onChange alone
      // only updates antd's in-memory form state which is destroyed on unmount.
      // we need this below to trigger the 'auto-save' feature immediately after map selection
      this.onChange.cancel();
      const { onBlur } = this.props;
      // Defer onBlur to the next task so each field gets its own React render
      // cycle and auto-save trigger. When multiple map fields dispatch onBlur
      // synchronously (from a single CustomEvent), React batches the Redux
      // updates into one render, and only one field's save-field call fires
      // because the isOwnChange flag is consumed once. setTimeout(0) breaks
      // them into separate tasks so each save cycle completes independently.
      setTimeout(() => {
        onBlur && onBlur(value);
      }, 0);
    }
  };

  componentWillUnmount() {
    if (this.state.isMapField && this.enableAddressVerification)
      window.removeEventListener(
        'form-component-map-value-change',
        this.onMapValueChange
      );
    this.onChange.cancel();
    this.inputRef = undefined;
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      propsHasChanged(nextProps, this.props) ||
      nextState.showMap !== this.state.showMap ||
      nextState.clearMap !== this.state.clearMap
    );
  }

  componentDidUpdate(prevProps, prevState) {
    const { value } = prevProps;
    const { value: currentValue, dependsOnChange, isReviewer } = this.props;
    if (!isReviewer && value !== currentValue && !dependsOnChange) {
      // we need to sync text input internal component state
      if (this.inputRef.current) {
        this.inputRef.current.setState({ value: this.props.value });
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      calculatedValue,
      isOwnChange,
      onChange,
      dependsOnChange,
      isReviewer,
      ...props
    } = nextProps;
    const { value: currentValue } = this.props;

    let realValue = props.value;
    if (!isReviewer) {
      if (calculatedValue !== realValue && !isOwnChange && dependsOnChange) {
        if (
          currentValue !== props.value ||
          (calculatedValue === undefined && currentValue === undefined) ||
          calculatedValue !== currentValue
        ) {
          onChange(calculatedValue);
        }
      }
    }
  }

  onChange = debounce(event => {
    const { onChange, onBlur } = this.props;
    if (!event || !event.target) return;
    const {
      target: { value },
    } = event;
    if (this.inputRef?.current?.input === document.activeElement) {
      // current focused element, user is typing
      onChange && onChange(value);
    } else {
      // not currently focused element, data is probably
      // coming from mapbox plugin, call onBlur to trigger auto-save
      onBlur && onBlur(value);
    }
  }, 2000);

  onClickShowMap = async event => {
    const { showMap = false } = this.state;
    const { value } = this.props;

    this.setState({
      showMap: !showMap,
      hasAddress: !!value,
    });
  };

  getMapIcon = () => {
    const { disabled } = this.props;

    return this.state.isMainMapField && this.state.isMapField ? (
      <Tooltip
        title="Click this button to look up your address on the map."
        zIndex="1"
      >
        <StyledMapIcon
          onClick={disabled ? undefined : this.onClickShowMap}
          disabled={disabled}
        >
          <StyledIcon type="global" />
        </StyledMapIcon>
      </Tooltip>
    ) : null;
  };

  handleOk = () => {
    this.setState({ showMap: false });
  };

  handleCancel = () => {
    this.setState({
      showMap: false,
    });
  };

  handleClear = () => {
    this.setState({ mapData: {}, clearMap: true });
  };

  saveMapData = mapData => {
    if (isEmpty(mapData)) {
      this.handleClear();
    } else {
      this.setState({ mapData, clearMap: false });
    }
  };

  getValue = key => {
    return getValue(key, this.props.selectedForm.data);
  };

  render() {
    const {
      enums,
      options,
      isReviewer,
      parentKey,
      parentIndex,
      dataKey,
      calculatedValue,
      data,
      isOwnChange,
      dependsOnChange,
      timezone,
      value = '',
      field_type,
      sourceFieldValue,
      ...props
    } = this.props;
    let overrideEditRights = false;
    if (this.props.isContributeReport && !this.props.dependsOn && !options[DISPLAY_FROM]) {
      if (this.props.contributorAssignmentCanEdit) {
        // I am assigned to this section
        overrideEditRights = true;
      } else if (this.props.contributorAssignmentCanView) {
        // I am the submitter OR have access to see drafts, so I can see all the content
        const _value = calculatedValue || value;
        // if the value is not escaped, it is probably recently added by the user and
        // inmediatly marked the section as completed, so we need to escape it to avoid XSS
        return (
          <ReviewerField
            value={looksEscaped(_value) ? _value : htmlEscape(_value)}
          />
        );
      } else {
        // I have no special rights for this section, hide
        return <ReviewerField />;
      }
    }
    const { autocomplete, disableInputAddress } = this.state;
    // normal reviewer-submitter piece
    if (isReviewer && !overrideEditRights) {
      return <ReviewerField value={calculatedValue || value} />;
    }

    if (sourceFieldValue && !overrideEditRights) {
      return (
        <Input
          bordered="false"
          className="read-only"
          size="default"
          value={htmlUnescape(get(sourceFieldValue, 'value', ''))}
          {...props}
        />
      );
    }

    return (
      <StyledInputWrapper>
        <Input
          className={`${
            this.state.isMapField
              ? ` map-field ${this.addressTypes.join(' ')}`
              : ''
          }`}
          size="default"
          {...omit(props, ['dispatch'])}
          defaultValue={htmlUnescape(value)}
          ref={this.inputRef}
          autocomplete={autocomplete}
          onChange={this.onChange}
          addonBefore={this.getMapIcon()}
          disabled={disableInputAddress || props.disabled}
        />

        {this.state.isMapField && this.state.showMap && (
          <MapComponent
            title={this.props.title}
            inputRef={this.inputRef}
            getValue={this.getValue} // this should be a function that can take the input id or data attribute and get value
            onOk={this.handleOk}
            onCancel={this.handleCancel}
            showLayers={true} // future property for shapes and boundaries
          />
        )}
      </StyledInputWrapper>
    );
  }
}

const lineBreak = '&#013;&#010;';
const lineBreakRegex = /&#013;&#010;/g;
const escapedLineBreakRegex = /&amp;#013;&amp;#010;/g;

class TextareaInput extends Component {
  state = {
    fieldName: '',
    fieldValue: '',
    fieldParentName: '',
    statusDraft: '',
    isDraftJustSaved: false,
    hasValueChanged: false,
  };

  shouldComponentUpdate(nextProps) {
    return propsHasChanged(nextProps, this.props);
  }

  componentDidUpdate(prevProps, prevState) {
    const { value } = prevProps;
    const { value: currentValue, dependsOnChange, isReviewer } = this.props;
    if (!isReviewer && value !== currentValue && !dependsOnChange) {
      if (this.textArea) {
        this.textArea.setState({ value: this.props.value });
      }
    }
  }

  componentWillUnmount() {
    this.onChange.cancel();
    this.onKeyDown.cancel();
  }

  formatData = (fieldParentName, fieldName, fieldValue) => {
    const {
      selectedForm: { data },
      parentIndex,
    } = this.props;
    const changedField = { [fieldName]: fieldValue };
    const changedFields = fieldParentName
      ? { [fieldParentName]: changedField }
      : changedField;
    let dataValue = getRepeaterPropsForAutoSave(changedFields, data);
    if (fieldParentName) {
      dataValue = {
        ...dataValue,
        [fieldParentName]: get(dataValue, fieldParentName).map((d, i) =>
          i === parentIndex ? { ...d, ...changedField } : d
        ),
      };
    }
    return dataValue;
  };

  statusIsDraft = () => {
    const {
      selectedForm,
      selectedForm: { meta },
      currentUser: { id },
    } = this.props;
    const statusInfo = getLastStatus(
      { actions: selectedForm.actions, ...meta },
      id
    );
    const { key: statusKey = 'draft' } = statusInfo;
    const isDraft = statusKey === 'draft' || !selectedForm.meta.id;
    return isDraft;
  };

  triggerAutoSave = () => {
    const {
      fieldName,
      fieldValue,
      fieldParentName,
      statusDraft,
      isDraftJustSaved,
      hasValueChanged,
    } = this.state;

    const {
      selectedForm,
      dispatch,
      data: {
        activeUser: id,
        meta: { workFlowData: { status = {} } = {} } = {},
      },
      isPreviewModeEnabled,
    } = this.props;

    if (isPreviewModeEnabled) {
      return;
    }

    const statusIsDraft = statusDraft
      ? statusDraft
      : has(status, id) && get(status, [id, 'labelIdentifier']) === 'DRAFT';

    if (hasValueChanged) {
      const dataValue = this.formatData(fieldParentName, fieldName, fieldValue);
      autoSaveDraftReport(
        statusIsDraft,
        selectedForm,
        dataValue,
        isDraftJustSaved,
        dispatch
      );
      this.setState({ isDraftJustSaved: true });
      this.setState({ hasValueChanged: false });
    }
  };

  setStateValuesToBeSave = debounce(
    (parentName, name, value, statusIsDraft) => {
      this.setState({ fieldParentName: parentName });
      this.setState({ fieldName: name });
      this.setState({ fieldValue: value });
      this.setState({ statusDraft: statusIsDraft });
      this.setState({ hasValueChanged: true });
    },
    500
  );

  onChange = debounce(e => {
    const {
      onChange,
      parentKey,
      dataKey,
      data: {
        activeUser: id,
        meta: { workFlowData: { status = {} } = {} } = {},
      },
    } = this.props;
    const { target: { value = '' } = {} } = e;

    const statusIsDraft =
      has(status, id) && get(status, [id, 'labelIdentifier']) === 'DRAFT';

    const sanitized = value.replace(/\n/g, lineBreak);
    onChange(sanitized);

    this.setStateValuesToBeSave(parentKey, dataKey, sanitized, statusIsDraft);
  }, 2000);

  onKeyDown = debounce(e => {
    if (this.statusIsDraft()) this.triggerAutoSave();
  }, AUTO_SAVE_TIME * 1000);

  render() {
    const {
      enums,
      options,
      isReviewer,
      dataKey,
      parentKey,
      parentIndex,
      onChange,
      calculatedValue,
      isOwnChange,
      dependsOnChange,
      timezone,
      field_type,
      value: currentValue = '',
      ...props
    } = this.props;
    const { minRows: rows = 4 } = options;
    let overrideEditRights = false;
    let overrideReviewer = isReviewer;

    if (this.props.isContributeReport && !this.props.dependsOn && !options[DISPLAY_FROM]) {
      if (this.props.contributorAssignmentCanEdit) {
        overrideEditRights = true;
      } else if (this.props.contributorAssignmentCanView) {
        overrideReviewer = true;
      } else {
        <ReviewerField></ReviewerField>;
      }
    }
    if (overrideReviewer && !overrideEditRights) {
      const text = currentValue || calculatedValue || '';
      const _value = looksEscaped(text) ? text : htmlEscape(text);
      const value = _value
        .replace(escapedLineBreakRegex, '<br />')
        .replace(/\n/g, '<br />');
      // if the value is not escaped, it is probably recently added by the user and
      // inmediatly marked the section as completed, so we need to escape it to avoid XSS
      return <ReviewerField value={value} />;
    }

    const value = (htmlUnescape(currentValue) || '').replace(
      lineBreakRegex,
      '\n'
    );
    return (
      <Input.TextArea
        spellCheck
        rows={rows}
        {...props}
        ref={ref => (this.textArea = ref)}
        defaultValue={value}
        onChange={this.onChange}
        onKeyDown={this.onKeyDown}
      />
    );
  }
}

const mapStateToProps = (state, props) => {
  const { form: { selectedForm } = {}, session: { currentUser } = {} } = state;
  return {
    sourceFieldValue: getSourceFieldValue(state, props),
    selectedForm,
    currentUser,
  };
};

export default connect(mapStateToProps)(InputText);
export const Textarea = connect(mapStateToProps)(TextareaInput);
